/**
 * Global Error Handler
 * Catches unhandled errors and prevents app crashes.
 * CRITICAL: Call BEFORE React renders. Logs error context (route, feature) so crashes are never silent.
 */

import { logger } from './logger';
import { getErrorContext } from './errorContext';

/**
 * Check if error is a database deletion or connection error
 */
export function isDatabaseDeletedError(error: any): boolean {
  if (!error) return false;
  
  const errorMessage = String(error?.message || error?.toString() || '').toLowerCase();
  const errorName = String(error?.name || '').toLowerCase();
  const errorString = String(error || '').toLowerCase();
  
  // Check for all variations of IndexedDB connection errors
  return (
    errorName === 'invalidstateerror' ||
    errorName === 'aborterror' ||
    errorName === 'domexception' ||
    errorMessage.includes('deleted') ||
    errorMessage.includes('database deleted') ||
    errorMessage.includes('database deleted by request') ||
    errorMessage.includes('database deleted by request of the user') ||
    errorMessage.includes('force close') ||
    errorMessage.includes('delete origin') ||
    errorMessage.includes('connection is closing') ||
    errorMessage.includes('connection is closing because of') ||
    errorMessage.includes('force close delete origin') ||
    errorMessage.includes('not found') ||
    errorMessage.includes('idbtransaction') ||
    errorMessage.includes('idb') ||
    error?.code === 0 ||
    errorString.includes('force close delete origin') ||
    errorString.includes('connection is closing because of') ||
    errorString.includes('aborterror') ||
    errorString.includes('idbtransaction.error') ||
    errorString.includes('idbtransaction') ||
    errorString.includes('idb') ||
    // Check if error string contains the exact message
    String(error).toLowerCase().includes('database deleted by request of the user') ||
    // Check error object properties
    (error?.target && error?.target?.error && isDatabaseDeletedError(error.target.error)) ||
    // Check for IDBTransaction errors specifically
    (error?.target?.source && error?.target?.source?.objectStore) ||
    // Check stack trace for IDBTransaction
    (error?.stack && (error.stack.includes('IDBTransaction') || error.stack.includes('idb')))
  );
}

/**
 * Handle database errors gracefully
 */
export function handleDatabaseError(error: any, context: string = 'Database operation'): void {
  if (isDatabaseDeletedError(error)) {
    logger.warn(`${context}: IndexedDB was deleted - app will continue without cache`);
    // Don't throw - allow app to continue
    return;
  }
  
  logger.error(`${context} failed:`, error);
  // Don't throw - errors are logged but don't crash the app
}

// Flag to prevent double initialization
let handlersInitialized = false;

/**
 * Suppress React's error overlay for specific errors
 * This patches the window.onerror before React can hook into it
 */
function suppressReactOverlay(): void {
  // Monkey-patch to suppress DOMException AbortError from React overlay
  const originalOnError = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    // Suppress IDB and AbortError from showing in overlay
    const msgStr = String(message || '').toLowerCase();
    const errStr = String(error?.name || '').toLowerCase();
    if (msgStr.includes('aborterror') || 
        msgStr.includes('idbtransaction') ||
        msgStr.includes('force close') ||
        errStr === 'aborterror' ||
        errStr === 'domexception') {
      return true; // Suppress error
    }
    if (originalOnError) {
      return originalOnError.call(window, message, source, lineno, colno, error);
    }
    return false;
  };
}

/**
 * Setup global error handlers
 * CRITICAL: Call this BEFORE React renders to catch IndexedDB errors
 */
export function setupGlobalErrorHandlers(): void {
  // Prevent double initialization
  if (handlersInitialized) return;
  handlersInitialized = true;
  
  // Suppress React's error overlay for IDB errors
  suppressReactOverlay();
  
  // EARLY CAPTURE: Add capturing listener to catch errors before React's overlay
  // This runs in the capture phase, before bubbling
  window.addEventListener('error', (event: ErrorEvent) => {
    const error = event.error || event.message;
    if (isDatabaseDeletedError(error)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    }
  }, true); // true = capture phase
  
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (isDatabaseDeletedError(event.reason)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
  }, true); // true = capture phase

  // Handle unhandled promise rejections (CRITICAL: Prevents browser crashes)
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const error = event.reason;
    
    // Check if it's a database deleted or connection error
    if (isDatabaseDeletedError(error)) {
      // Suppress these errors - they're expected when IndexedDB connections close
      event.preventDefault(); // Prevent error from showing in console
      event.stopPropagation(); // Stop propagation
      return;
    }
    
    // Check for network/API errors (common in AI calls) - don't crash on these
    const errorMessage = String(error?.message || error?.toString() || '').toLowerCase();
    if (errorMessage.includes('network') || 
        errorMessage.includes('fetch') || 
        errorMessage.includes('timeout') ||
        errorMessage.includes('aborted') ||
        error?.code === 'ECONNABORTED' ||
        error?.name === 'AbortError') {
      // Network errors are non-fatal - log but don't crash
      logger.warn('Unhandled promise rejection (network):', error);
      event.preventDefault();
      return;
    }
    
    // Check for memory-related errors (could indicate memory leak)
    if (errorMessage.includes('memory') || 
        errorMessage.includes('out of memory') ||
        (error?.name === 'RangeError' && errorMessage.includes('maximum call stack'))) {
      // Memory errors are critical - log with warning
      logger.error('Memory-related error detected:', error);
      event.preventDefault(); // Prevent crash but log for debugging
      return;
    }
    
  // Log all rejections with context so no silent crashes
  const ctx = getErrorContext();
  logger.error('Unhandled promise rejection:', { error, route: ctx.route, feature: ctx.feature });
  logger.lifecycle('global', 'unhandledrejection', { reason: String(event.reason), route: ctx.route });
  event.preventDefault();
  });

  // Handle general errors (render + runtime)
  window.addEventListener('error', (event: ErrorEvent) => {
    const error = event.error || event.message;

    if (isDatabaseDeletedError(error)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const ctx = getErrorContext();
    logger.error('Global error:', { error, route: ctx.route, feature: ctx.feature });
    logger.lifecycle('global', 'error', { message: String(error), route: ctx.route });
    event.preventDefault();
  });
  
  // Handle console errors (catch IDBTransaction errors that bubble to console)
  const originalError = console.error;
  console.error = (...args: any[]) => {
    // Check if any argument is a database error
    const isDBError = args.some(arg => isDatabaseDeletedError(arg));
    if (isDBError) {
      // Suppress database errors from console
      return;
    }
    // Log other errors normally
    originalError.apply(console, args);
  };
  
  logger.info('Global error handlers initialized');
}







